import * as React from 'react';
import { useEffect, useCallback, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext, UseFieldArrayRemove } from 'react-hook-form';
import { DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { sliceContent, useMediaFileUpload } from '@restworld/utils-common';
import { Upload, UploadType, uploadObject } from '@restworld/ui-components';
import { Heading, Button } from '@restworld/ui-ds-base';

import { WizardStepProps } from '../../worker-application-wizard.types';

export const WizardTenthStep: React.FunctionComponent<WizardStepProps> = () => (
  <div className="flex flex-col gap-4">
    <UploadFormComponent uploadType="picture" key="picture" testId="worker-picture-input" />
    <UploadFormComponent uploadType="video" key="video" />
  </div>
);
export default WizardTenthStep;
const UploadFormComponent = ({ uploadType, testId }: { uploadType: UploadType; testId?: string }) => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: uploadType,
  });
  const mappedItem = useMemo(() => uploadObject[uploadType], [uploadType]);
  const uploadText = useMemo(() => mappedItem['text'], [mappedItem]);

  useEffect(() => {
    if (!fields.length) append({});
  }, [append, fields.length, uploadType]);
  return (
    <div className="flex flex-col gap-4">
      <Heading size={6} weight="normal" color="muted">
        {uploadText === 'video' ? t('common:your_video') : t('common:your_picture')}
      </Heading>
      {[...fields].map((field, fieldIndex) => (
        <div className="flex flex-col gap-2" key={field.id}>
          <MediaContent uploadType={uploadType} fieldIndex={fieldIndex} removeFn={remove} testId={testId} />
        </div>
      ))}
      <div className="self-center">
        <Button
          onClick={(e) => {
            e?.preventDefault();
            append({});
          }}
          variant="secondary-restworld"
        >
          {`+ ${t('add')} ${uploadText}`}
        </Button>
      </div>
    </div>
  );
};
const MediaContent = ({
  uploadType,
  fieldIndex,
  removeFn,
  testId,
}: {
  uploadType: UploadType;
  fieldIndex: number;
  removeFn: UseFieldArrayRemove;
  testId?: string;
}) => {
  const { control, setValue, getValues } = useFormContext();
  const [uploadFiles, setUploadFiles] = useState<File>();
  const { uploadFile, deleteUploadedFile, loading } = useMediaFileUpload();
  const handleUploadChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.[0]) {
        const resp = await uploadFile({ file: event.target.files[0], type: uploadType });
        if (resp?.['url']) {
          setUploadFiles(event.target.files[0]);
          setValue(`${uploadType}.${fieldIndex}`, { url: resp?.['url'], uploadFiles: event.target.files[0] });
        }
      }
    },
    [fieldIndex, setValue, uploadFile, uploadType]
  );
  const { url = '', uploadFiles: uploadFilesCopy } = useMemo(
    () => getValues(`${uploadType}.${fieldIndex}`) || {},
    [fieldIndex, getValues, uploadType]
  );
  const uploadFileCp: File = uploadFiles || uploadFilesCopy;
  return uploadFileCp ? (
    <UploadedMedia
      uploadFiles={uploadFileCp}
      loading={loading}
      handleDelete={() => {
        if (url) {
          deleteUploadedFile({ url });
          removeFn(fieldIndex);
        }
      }}
    />
  ) : (
    <Controller
      name={`${uploadType}.${fieldIndex}`}
      control={control}
      render={({ field }) => (
        <Upload
          {...{
            field,
            onChange: handleUploadChange,
          }}
          uploadType={uploadType}
          loading={loading}
          key={uploadType}
          testId={testId}
        />
      )}
    />
  );
};
const UploadedMedia = ({
  uploadFiles,
  handleDelete,
  loading,
}: {
  uploadFiles: File;
  handleDelete: () => void;
  loading?: boolean;
}) => (
  <div className="bg-primary-50 flex gap-4 items-center justify-between rounded-lg w-full p-3">
    <div className="flex gap-4 items-center">
      <div className="rounded-lg border bg-white p-3">
        <DocumentTextIcon className="h-6 w-6 text-primary-800" />
      </div>
      {uploadFiles ? (
        <div className="flex flex-col gap-2">
          <Heading size={6}>{uploadFiles?.name && sliceContent(uploadFiles?.name, 20)}</Heading>
          <Heading size={6} weight="medium" color="muted">{`${(uploadFiles?.size / 1000000).toFixed(2)}MB`}</Heading>
        </div>
      ) : null}
    </div>
    <Button
      loading={loading}
      StartIcon={TrashIcon}
      variant="secondary-danger"
      onClick={(e) => {
        handleDelete();
      }}
    />
  </div>
);
